import { Outlet, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Calendar as CalendarIcon, Users, CreditCard,
  LogOut, Search, Bell, GraduationCap, Clock, Stethoscope, Settings,
  Package, FileText, BarChart3, Menu, ClipboardList, BookOpen, Shield
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { AdminProvider, useAdmin } from '@/contexts/AdminContext';
import { BookingDraftProvider } from '@/contexts/BookingDraftContext';
import BookingDraftManager from '@/components/admin/BookingDraftManager';
import { Toaster } from 'sonner';
import { cn } from '@/lib/utils';

/* ─── Nav helpers ────────────────────────────── */
function NavSection({ label, children }) {
  return (
    <div className="space-y-0.5">
      {label && (
        <p className="px-3 pt-5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-400">
          {label}
        </p>
      )}
      {children}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, badge }) {
  const location = useLocation();
  const isOverview = to === '/admin' || to === '/admin/dashboard';
  const active = isOverview
    ? location.pathname === '/admin' || location.pathname === '/admin/' || location.pathname === '/admin/dashboard'
    : location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link to={to}>
      <div className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200',
        active
          ? 'bg-stone-900 text-white shadow-md shadow-stone-900/10'
          : 'text-stone-500 hover:bg-stone-100 hover:text-stone-800'
      )}>
        <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={active ? 2 : 1.5} />
        <span className="text-[13px] font-medium flex-1">{label}</span>
        {badge && (
          <span className={cn(
            'inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1',
            active ? 'bg-white/20 text-white' : 'bg-stone-200 text-stone-600'
          )}>
            {badge}
          </span>
        )}
      </div>
    </Link>
  );
}

/* ─── Inner layout ───────────────────────────── */
function AdminLayoutInner() {
  const { user, logout, hasLDA } = useAdmin();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const sidebar = (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="h-16 flex items-center px-5 border-b border-stone-200/80">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-stone-900 flex items-center justify-center">
            <Stethoscope className="w-4 h-4 text-white" />
          </div>
          <h1 className="font-playfair font-bold text-lg text-stone-900 tracking-tight">Admin</h1>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
        <NavSection>
          <NavItem to="/admin/dashboard" icon={LayoutDashboard} label="Overview" />
        </NavSection>

        <NavSection label="Operations">
          <NavItem to="/admin/bookings" icon={ClipboardList} label="Bookings" />
          <NavItem to="/admin/calendar" icon={CalendarIcon} label="Calendar" />
          <NavItem to="/admin/schedule" icon={Clock} label="Schedule" />
        </NavSection>

        <NavSection label="Catalogue">
          <NavItem to="/admin/services" icon={Package} label="Services" />
          <NavItem to="/admin/providers" icon={Stethoscope} label="Providers" />
        </NavSection>

        <NavSection label="People">
          <NavItem to="/admin/patients" icon={Users} label="Patients" />
        </NavSection>

        <NavSection label="Finance">
          <NavItem to="/admin/payments" icon={CreditCard} label="Payments" />
        </NavSection>

        {hasLDA && (
          <NavSection label="Academy">
            <NavItem to="/admin/delegates" icon={GraduationCap} label="Delegates" />
            <NavItem to="/admin/courses" icon={BookOpen} label="Courses & Intakes" />
            <NavItem to="/admin/materials" icon={FileText} label="Materials" />
            <NavItem to="/admin/analytics" icon={BarChart3} label="Analytics" />
          </NavSection>
        )}

        <NavSection>
          {user?.role === 'owner' && <NavItem to="/admin/team" icon={Shield} label="Team" />}
          <NavItem to="/admin/settings" icon={Settings} label="Settings" />
        </NavSection>
      </nav>

      {/* User + Logout */}
      <div className="p-4 border-t border-stone-200/80 space-y-2">
        <div className="flex items-center gap-3 px-2 py-1">
          <Avatar className="h-8 w-8 border border-stone-200">
            <AvatarFallback className="bg-stone-900 text-white text-xs font-semibold">
              {user?.name?.charAt(0) || '?'}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-stone-800 truncate">{user?.name}</p>
            <p className="text-[10px] text-stone-400 uppercase tracking-wider">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-stone-400 hover:bg-red-50 hover:text-red-600 transition-colors w-full"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-[13px] font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-stone-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <motion.aside
        initial={{ x: -260 }}
        animate={{ x: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="hidden lg:flex w-[260px] bg-white border-r border-stone-200/80 flex-col shrink-0"
      >
        {sidebar}
      </motion.aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 z-50 w-[280px] bg-white border-r border-stone-200 shadow-2xl"
            >
              {sidebar}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 bg-white border-b border-stone-200/80 flex items-center justify-between px-4 lg:px-8 z-10 shrink-0">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden h-9 w-9 rounded-lg border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
            >
              <Menu className="w-4 h-4 text-stone-600" />
            </button>
            <div className="hidden md:block flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
                <Input
                  placeholder="Search bookings, patients, services..."
                  className="pl-10 bg-stone-50/80 border-stone-200 focus-visible:ring-1 focus-visible:ring-stone-400 h-9 text-sm"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button className="relative text-stone-400 hover:text-stone-600 transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          <div className="p-4 lg:p-8 max-w-[1400px]">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </div>
        </div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminProvider>
      <BookingDraftProvider>
        <AdminLayoutInner />
        <BookingDraftManager />
      </BookingDraftProvider>
    </AdminProvider>
  );
}
